/**
 * AuditTimeline — chronologische Anzeige von Audit-Log-Einträgen.
 *
 * Generisch über das Item-Format (T): die Page liefert pro Item eine
 * `render`-Funktion die Headline + Body produziert. Die Komponente
 * kümmert sich um die zeitliche Anordnung, Group-by-Day, Empty-State.
 *
 * Beispiel:
 *   <AuditTimeline<TServiceAuditEntry>
 *     entries={audit.entries}
 *     timestampOf={(e) => e.changed_at}
 *     render={(e) => ({
 *       headline: `${e.field}: ${e.old_value ?? '–'} → ${e.new_value ?? '–'}`,
 *       meta: `${e.changed_by} · ${formatDate(e.changed_at)}`,
 *       body: e.change_reason,
 *     })}
 *   />
 */

import type { ReactNode } from 'react'
import './AuditTimeline.css'

export interface AuditTimelineRenderResult {
  headline: ReactNode
  meta?: ReactNode
  body?: ReactNode
  /** Variant beeinflusst den Marker-Punkt (Farbe). */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info'
}

export interface AuditTimelineProps<T> {
  entries: T[]
  timestampOf: (entry: T) => string | Date
  render: (entry: T) => AuditTimelineRenderResult
  /** Sortierreihenfolge — default: neueste zuerst */
  sort?: 'desc' | 'asc'
  /** Gruppiere Einträge nach Datum (lokale Zone) */
  groupByDay?: boolean
  emptyMessage?: ReactNode
  className?: string
}

export function AuditTimeline<T>({
  entries,
  timestampOf,
  render,
  sort = 'desc',
  groupByDay = true,
  emptyMessage = 'Noch keine Audit-Einträge',
  className = '',
}: AuditTimelineProps<T>) {
  if (!entries || entries.length === 0) {
    return <p className={`audit-timeline-empty ${className}`.trim()}>{emptyMessage}</p>
  }

  const withTs = entries.map((e) => ({ entry: e, ts: new Date(timestampOf(e)) }))
  withTs.sort((a, b) => sort === 'desc' ? b.ts.getTime() - a.ts.getTime() : a.ts.getTime() - b.ts.getTime())

  const groups = groupByDay
    ? groupByDayLocal(withTs)
    : [{ key: 'all', label: '', items: withTs }]

  return (
    <div className={`audit-timeline ${className}`.trim()}>
      {groups.map((group) => (
        <section key={group.key} className="audit-timeline-group">
          {group.label && <div className="audit-timeline-day">{group.label}</div>}
          <ol className="audit-timeline-list">
            {group.items.map(({ entry, ts }, idx) => {
              const r = render(entry)
              return (
                <li
                  key={`${group.key}-${idx}`}
                  className={`audit-timeline-item audit-timeline-item-${r.variant ?? 'default'}`}
                >
                  <div className="audit-timeline-marker" aria-hidden />
                  <div className="audit-timeline-content">
                    <div className="audit-timeline-headline">{r.headline}</div>
                    {r.meta && <div className="audit-timeline-meta">{r.meta}</div>}
                    {r.body && <div className="audit-timeline-body">{r.body}</div>}
                    <time className="audit-timeline-time">{formatTime(ts)}</time>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      ))}
    </div>
  )
}

interface Group<T> { key: string; label: string; items: { entry: T; ts: Date }[] }

function groupByDayLocal<T>(items: { entry: T; ts: Date }[]): Group<T>[] {
  const map = new Map<string, Group<T>>()
  for (const it of items) {
    const key = it.ts.toISOString().slice(0, 10) // YYYY-MM-DD
    if (!map.has(key)) {
      map.set(key, { key, label: formatDay(it.ts), items: [] })
    }
    map.get(key)!.items.push(it)
  }
  return Array.from(map.values())
}

function formatDay(d: Date): string {
  return d.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export default AuditTimeline
